import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import BrandLogo from '@/components/BrandLogo';
import { toast } from '../utils/toast';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Email, 2: OTP & New Password
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    otp: '',
    new_password: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error('Email address is required.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: formData.email
      });
      toast.success(response.data.message);
      setStep(2);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'An error occurred.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!formData.otp) {
      toast.error('OTP is required.');
      return;
    }
    if (!formData.new_password) {
      toast.error('New password is required.');
      return;
    }
    if (formData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters long.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        email: formData.email,
        otp: formData.otp,
        new_password: formData.new_password
      });
      toast.success(response.data.message);
      setTimeout(() => {
        navigate('/signin');
      }, 2000);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'An error occurred.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F6FA] via-white to-[#E8F4F8] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8 fade-in">
          <div className="inline-flex items-center justify-center mb-4">
            <BrandLogo />
          </div>
          <h1 className="text-[22px] font-semibold mb-2" style={{ color: '#0056B3' }}>
            {step === 1 ? 'Forgot Password?' : 'Reset Password'}
          </h1>
          <p className="text-gray-600">
            {step === 1 
              ? 'Enter your email to receive a verification code'
              : 'Enter the OTP sent to your email and create a new password'
            }
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl p-8 shadow-xl card-shadow slide-in-left">
          {step === 1 ? (
            <form onSubmit={handleSendOTP} className="space-y-6" data-testid="forgot-password-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Registered Email Address
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="forgot-email-input"
                  placeholder="Enter your registered email address"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full rounded-lg border-gray-300 focus:border-[#0056B3] focus:ring-[#0056B3]"
                />
              </div>

              <Button
                type="submit"
                data-testid="send-otp-btn"
                disabled={loading}
                className="w-full bg-[#0056B3] hover:bg-[#0056B3]/90 text-white rounded-lg py-6 text-base font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send One Time Password'
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-6" data-testid="reset-password-form">
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-sm font-medium text-gray-700">
                  One-Time Password (OTP)
                </Label>
                <Input
                  id="otp"
                  name="otp"
                  type="text"
                  data-testid="otp-input"
                  placeholder="Enter 6-digit OTP"
                  value={formData.otp}
                  onChange={handleChange}
                  maxLength={6}
                  className="w-full rounded-lg border-gray-300 focus:border-[#0056B3] focus:ring-[#0056B3] text-center text-2xl tracking-widest"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new_password" className="text-sm font-medium text-gray-700">
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    name="new_password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="new-password-input"
                    placeholder="Enter new password"
                    value={formData.new_password}
                    onChange={handleChange}
                    className="w-full rounded-lg border-gray-300 focus:border-[#0056B3] focus:ring-[#0056B3] pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#0056B3]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500">Password must be at least 8 characters long</p>
              </div>

              <Button
                type="submit"
                data-testid="reset-password-btn"
                disabled={loading}
                className="w-full bg-[#0056B3] hover:bg-[#0056B3]/90 text-white rounded-lg py-6 text-base font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium"
                >
                  ← Back to email entry
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Remember your password?{' '}
              <button
                onClick={() => navigate('/signin')}
                data-testid="signin-link"
                className="text-[#0056B3] hover:text-[#00AEEF] font-medium"
              >
                Sign In
              </button>
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-600 hover:text-[#0056B3]"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;